import { Link } from "react-router";
import { EmptyState } from "@repo/blocks";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useVenue } from "../../shell/VenueContext";
import { ChannelBadge, StatusBadge } from "../Badges";
import { bookingWhen, guestName, isNum } from "../format";
import { flattenPages, useBookingsList } from "../queries";
import { isNarrowed, type BookingFilter } from "../searchParams";
import { banner } from "../styles";

/**
 * List view: the same booking dataset as a cursor-paginated table (ADR-0022 — `nextCursor` only, no
 * offset). It refuses an unbounded query until a filter narrows it (wide-range guard) and shows the
 * six status colours + channel badges; an unmapped channel is surfaced, never guessed (R6).
 */
export function ListView({
  filter,
  onSelect,
  onCreate,
}: {
  filter: BookingFilter;
  onSelect: (b: StaffBookingSummaryResponse) => void;
  /** Opens the manual-entry dialog — wired to the empty-state CTA. */
  onCreate?: () => void;
}) {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const query = useBookingsList(venueId, filter);
  const rows = flattenPages(query.data);

  if (!isNarrowed(filter)) {
    return (
      <div role="note" className={banner("info")} data-testid="narrow-prompt">
        {t("bookings.list.narrowPrompt")}
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.list.loading")}</p>
    );
  }

  if (rows.length === 0) {
    return (
      <div role="note">
        <EmptyState
          icon="📋"
          title={t("bookings.list.emptyTitle")}
          description={t("bookings.list.emptyBody")}
          action={onCreate ? { label: t("bookings.action.new"), onClick: onCreate } : undefined}
        />
        <p
          className={css({
            textAlign: "center",
            fontSize: "sm",
            color: "fg.muted",
            mt: "-4",
            mb: "6",
          })}
        >
          {t("bookings.list.emptyShare")}{" "}
          <Link
            to={`/v/${venueId}/settings/embed`}
            className={css({
              color: "colorPalette.text",
              textDecoration: "underline",
              fontWeight: "medium",
            })}
          >
            {t("bookings.list.emptyShareLink")}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      <div className={css({ overflowX: "auto" })}>
        <table className={css({ width: "full", borderCollapse: "collapse", fontSize: "sm" })}>
          <caption className={css({ srOnly: true })}>{t("bookings.list.caption")}</caption>
          <thead>
            <tr className={css({ textAlign: "left", color: "fg.muted", borderBottomWidth: "1px" })}>
              <th className={th}>{t("bookings.field.time")}</th>
              <th className={th}>{t("bookings.field.guest")}</th>
              <th className={th}>{t("bookings.field.party")}</th>
              <th className={th}>{t("bookings.field.status")}</th>
              <th className={th}>{t("bookings.field.channel")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.bookingId} className={css({ borderBottomWidth: "1px" })}>
                <td className={td}>
                  {isNum(b.startSec) ? bookingWhen(b.startSec, venue.timeZone, i18n.language) : "—"}
                </td>
                <td className={td}>
                  <button
                    type="button"
                    className={css({
                      color: "colorPalette.text",
                      textDecoration: "underline",
                      fontWeight: "medium",
                      _hover: { color: "colorPalette.emphasized" },
                    })}
                    onClick={() => onSelect(b)}
                  >
                    {guestName(b, t("bookings.noName"))}
                  </button>
                </td>
                <td className={td}>{b.partySize ?? "—"}</td>
                <td className={td}>
                  <StatusBadge status={b.status} />
                </td>
                <td className={td}>
                  <ChannelBadge channel={b.channel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {query.hasNextPage ? (
        <button
          type="button"
          className={css({
            alignSelf: "center",
            px: "4",
            py: "2",
            rounded: "md",
            borderWidth: "1px",
            fontSize: "sm",
            _disabled: { opacity: "0.5" },
          })}
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? t("bookings.list.loading") : t("bookings.list.loadMore")}
        </button>
      ) : null}
    </div>
  );
}

const th = css({ py: "2", pr: "4", fontWeight: "medium", whiteSpace: "nowrap" });
const td = css({ py: "2", pr: "4", verticalAlign: "middle" });
